// Dominio del producto DESCARGABLE. Sin slots/hosting, sin inteligencia (D2).

export interface UserProfile {
  id: string;
  email: string;
}

export type Plan = 'downloadable';

// Estados alineados con Stripe (la "pasarela" de usuarios se decide con esto).
export type EntitlementStatus = 'active' | 'trialing' | 'past_due' | 'canceled' | 'inactive';

export interface Entitlement {
  status: EntitlementStatus;
  plan: Plan | null;
  currentPeriodEnd?: string | null;
  // El usuario podría ser cliente hosted aunque no tenga el descargable:
  hasHostedAccount?: boolean;
  // D16 (trial sin tarjeta): ¿existe fila de suscripción (aunque esté caducada)?
  // false = nunca probó → puede empezar el trial. true = trial ya consumido.
  hasRecord?: boolean;
  // ¿La gestiona Stripe (checkout/webhook) o es un trial sin tarjeta?
  viaStripe?: boolean;
}

export type ModuleKey = 'collect' | 'train_troops' | 'auto_hunt';
export type DesiredState = 'run' | 'stop';

// Claves internas de recursos (las que entiende el cliente; heredadas del núcleo).
export type ResourceKey = 'carne' | 'madera' | 'carbon' | 'hierro';
export type TrainingUnitKey = 'infantry' | 'lancer' | 'marksman';
export type HuntType = 'beast' | 'terror';
export type GameLanguage = 'en' | 'es';

// Ajustes por módulo (paridad con el worker hosted).
export interface BotSettings {
  game_language: GameLanguage;
  collect: {
    resources: ResourceKey[];
    level: number;          // 1..10
    marches: number;        // 1..6
    gather_time: string | null; // hh:mm:ss (opcional)
    travel_time: string | null;
  };
  train: {
    units: TrainingUnitKey[];
    amount_percent: number; // 0..100
  };
  hunt: {
    type: HuntType;
    level: number;              // beast 1..30, terror 1..20
    rally_wait_minutes: 3 | 5 | 10 | 15;
    marches: number;            // 1..6
    // Críptidos primero (doc 12 · 0.5): si hay evento Y cuernos, el ciclo de
    // caza gasta el cuerno en un críptido; si no, caza normal (que los genera).
    cryptids: boolean;
  };
  extras: {
    help: boolean;
    heal: boolean;
    shield: boolean;
    // Dailies (B1): maestro + granular por función. El maestro (dailies) es el
    // que el cliente ya lee; los granulares afinan qué se hace.
    dailies?: boolean;
    daily_mail?: boolean;
    daily_heroes?: boolean;
    daily_idle?: boolean;
    daily_intel?: boolean;
    // Mapeo de Intelligence (0.6.4, doc 17): maestro de la sección; los
    // parámetros (región, celdas por pasada) viven en settings.intel.
    intelligence?: boolean;
  };
  // Mapeo de Intelligence (0.6.4, doc 17 §5): el cliente lee region_id (región
  // que mapean los bots de este usuario) y cells_per_pass (presupuesto de
  // celdas por turno de mapeo antes de devolver el control al resto de tareas).
  intel: {
    region_id: string | null;   // 3-5 dígitos; null = sin configurar
    cells_per_pass: number;     // 4 | 6 | 10
    // 0.6.17 · Shield Monitor: la sección Inteligencia es EXCLUYENTE — o el
    // bot mapea, o vigila el escudo de un jugador (nunca las dos).
    mode?: IntelMode;
    watch?: {
      x: number | null;
      y: number | null;
      // Región del OBJETIVO (0.6.31). Sin ella, el bot usaba su región de
      // Inteligencia: un objetivo de OTRA región se vigilaba en la equivocada
      // (mismas X/Y, castillo de otro jugador). null = región del bot.
      region?: string | null;
      interval_s: number;       // >= 60 (cada comprobación cuesta ~30 s)
    };
  };
}

export type IntelMode = 'mapping' | 'shield_monitor';

// Intervalo mínimo real entre comprobaciones: navegar al objetivo y leer su
// modal cuesta ~30 s, y la vigilancia corre en los huecos idle del bot.
export const SHIELD_WATCH_MIN_INTERVAL_S = 60;

export function defaultSettings(): BotSettings {
  return {
    game_language: 'en',
    collect: { resources: ['hierro'], level: 6, marches: 1, gather_time: null, travel_time: null },
    train: { units: [], amount_percent: 100 },
    hunt: { type: 'beast', level: 1, rally_wait_minutes: 3, marches: 1, cryptids: false },
    extras: {
      help: false, heal: false, shield: false,
      // daily_intel fuera de defaults (2026-07-27): Intel missions en desarrollo,
      // sin toggle en el dash; el cliente la ignora en duro aunque llegue true.
      dailies: false, daily_mail: true, daily_heroes: true, daily_idle: true,
      // Mapeo de Intelligence: ON por defecto (dueño 2026-08-02 — función
      // clave del producto; antes OFF por D0). Con region_id null el cliente
      // 0.6.16 AUTODETECTA la región de la cuenta vinculada.
      intelligence: true
    },
    intel: {
      region_id: null, cells_per_pass: 6,
      // Por defecto se MAPEA (gana tokens). El Shield Monitor es una elección
      // explícita del usuario porque desactiva el mapeo en ese bot.
      mode: 'mapping',
      watch: { x: null, y: null, region: null, interval_s: 300 }
    }
  };
}

export function mergeSettings(partial: Partial<BotSettings> | null | undefined): BotSettings {
  const base = defaultSettings();
  if (!partial) return base;
  return {
    game_language: partial.game_language === 'es' ? 'es' : 'en',
    collect: { ...base.collect, ...(partial.collect ?? {}) },
    train: { ...base.train, ...(partial.train ?? {}) },
    hunt: { ...base.hunt, ...(partial.hunt ?? {}) },
    extras: { ...base.extras, ...(partial.extras ?? {}) },
    intel: {
      ...base.intel,
      ...(partial.intel ?? {}),
      // `watch` se funde por campos: un cliente antiguo (sin watch) no debe
      // borrar el objetivo configurado, ni al revés.
      watch: { ...base.intel.watch!, ...((partial.intel as BotSettings['intel'] | undefined)?.watch ?? {}) }
    }
  };
}

// Snapshots que reporta el cliente (forma serializada de MarchQueueStatus /
// TrainingQueueStatus del núcleo; ver client/shell/game_loop.py).
export interface QueueSnapshotRow {
  queue: number;
  status: string;
  remaining?: string | null;
  unit?: string | null;
}

export interface BotSnapshots {
  marches?: QueueSnapshotRow[];
  training?: QueueSnapshotRow[];
}

export interface BotStatus {
  running: boolean;
  state: string;
  text: string;
  modules: Record<ModuleKey, boolean>;
  desired: DesiredState;
  settings: BotSettings;
  updatedAt?: string | null;
  lastReportAt?: string | null;
  clientVersion?: string | null;
  captureB64?: string | null;
  captureAt?: string | null;
  snapshots?: BotSnapshots | null;
  // 0.3.0 (doc 12 p4): código de actividad neutro + parámetros; la web lo
  // traduce con su i18n. `text` queda como fallback ES de clientes antiguos.
  statusCode?: string | null;
  statusParams?: Record<string, string | number> | null;
}

// Multi-instancia (0.6, doc 11): una fila = un bot (user_id, instance_id).
// El tope real vive en el SERVIDOR (trigger de la migración 008); este valor
// es solo para pintar el contador N/5 — mantener en sincronía.
export const MAX_INSTANCES = 5;

export interface BotSummary {
  instanceId: string;
  instanceLabel: string | null;
  running: boolean;
  state: string;
  statusCode?: string | null;
  statusParams?: Record<string, string | number> | null;
  text: string;
  desired: DesiredState;
  lastReportAt?: string | null;
  clientVersion?: string | null;
}

export const MODULE_LABELS: Record<ModuleKey, string> = {
  collect: 'Recolectar recursos',
  train_troops: 'Entrenar tropas',
  auto_hunt: 'Caza automática'
};

export const RESOURCE_LABELS: Record<ResourceKey, string> = {
  carne: 'Carne (Meat)',
  madera: 'Madera (Wood)',
  carbon: 'Carbón (Coal)',
  hierro: 'Hierro (Iron)'
};

export const UNIT_LABELS: Record<TrainingUnitKey, string> = {
  infantry: 'Infantería',
  lancer: 'Lanceros',
  marksman: 'Tiradores'
};

export const HUNT_TYPE_LABELS: Record<HuntType, string> = {
  beast: 'Bestia (10 vigor)',
  terror: 'Terror polar (25 vigor)'
};

export const LANGUAGE_LABELS: Record<GameLanguage, string> = {
  en: 'Inglés (por defecto)',
  es: 'Español'
};

// -- Intelligence (doc 13): el descargable CONSULTA datos cacheados por los
// bots hosted (map_targets). Tipos agnósticos de producto, calcados del hosted.
export type IntelligenceRequestStatus =
  | 'pending' | 'processing' | 'completed' | 'failed' | 'not_found'
  | 'searching_cache' | 'ready' | 'expired' | 'insufficient_data' | 'stale';

export interface IntelligenceMapTarget {
  playerName?: string | null;
  allianceTag?: string | null;
  allianceName?: string | null;
  power?: number | null;
  castleX?: number | null;
  castleY?: number | null;
  lastSeenAt?: string | null;
  confidence?: number | null;
}

export interface IntelligenceRequest {
  id: string;
  playerName: string;
  regionId?: string | null;
  // 'region' = volcado de región completa (jugador vacío); el resto, búsqueda
  // clásica por nombre ('player_or_alliance' / 'player').
  queryType?: string | null;
  status: IntelligenceRequestStatus;
  error?: string | null;
  createdAt: string;
  updatedAt?: string | null;
  hiddenAt?: string | null;
  results: IntelligenceMapTarget[];
}

// -- Soporte (doc 14): chat usuario <-> atención al cliente desde el dashboard.
export type SupportSender = 'user' | 'support';

export interface SupportMessage {
  id: string;
  sender: SupportSender;
  body: string;
  // URL lista para <img> (firmada en supabase; objectURL en mock) o null
  attachmentUrl?: string | null;
  createdAt: string;
  readAt?: string | null;
}
